import { useTranslation } from "react-i18next";
import AWSRegionSelect from "../../common/AWSRegionSelect/AWSRegionSelect";
import AmazonConnectConnector, {
  AmazonConnectFormValues,
} from "../models/AmazonConnector";
import { ConnectorConfigFormProps } from "../models/Connector";
import {
  TextField,
  FormGroup,
  FormHelperText,
  Box,
  Typography,
  Divider,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";

export default function AmazonConnectConfigForm({
  defaultValues,
  register,
  errors,
  control,
}: ConnectorConfigFormProps<AmazonConnectFormValues, "AmazonConnect">) {
  const { t } = useTranslation(["amazonConnect"]);
  const registerPrefix = AmazonConnectConnector.Type;
  const { watch, setValue } = useFormContext();

  function updateExternalBucketFields(shouldUseExternalBucket: boolean) {
    if (!shouldUseExternalBucket) {
      setValue(`${registerPrefix}.RecordingDestinationBucketName`, undefined);
      setValue(`${registerPrefix}.RecordingDestinationBucketRegion`, undefined);
    }
  }

  return (
    <>
      <Typography variant="subtitle2" color="primary">
        {t("amazonConnect:resourceConfiguration")}
      </Typography>
      <Divider />

      <FormGroup>
        <Box mb={2}>
          <TextField
            {...register(`${registerPrefix}.AmazonConnectARN`)}
            label={t("amazonConnect:amazonConnectARN")}
            defaultValue={defaultValues.AmazonConnectARN}
            error={!!errors?.AmazonConnect?.AmazonConnectARN}
            autoComplete="off"
            required
            fullWidth
          />
          {errors?.AmazonConnect?.AmazonConnectARN?.message && (
            <FormHelperText error>
              {t(errors.AmazonConnect.AmazonConnectARN.message)}
            </FormHelperText>
          )}
        </Box>

        <Box mb={2}>
          <TextField
            {...register(`${registerPrefix}.RecordingBucketName`)}
            label={t("amazonConnect:recordingBucketName")}
            defaultValue={defaultValues.RecordingBucketName}
            error={!!errors?.AmazonConnect?.RecordingBucketName}
            autoComplete="off"
            required
            fullWidth
          />
          {errors?.AmazonConnect?.RecordingBucketName?.message && (
            <FormHelperText error>
              {t(errors.AmazonConnect.RecordingBucketName.message)}
            </FormHelperText>
          )}
        </Box>

        <Box mb={2}>
          <AWSRegionSelect
            label={t("amazonConnect:recordingBucketRegion")}
            {...register(`${registerPrefix}.RecordingBucketRegion`, {
              required: true,
            })}
            defaultValue={defaultValues.RecordingBucketRegion}
            error={!!errors?.AmazonConnect?.RecordingBucketRegion}
            required
          />
          {errors?.AmazonConnect?.RecordingBucketRegion?.message && (
            <FormHelperText error>
              {t(errors.AmazonConnect.RecordingBucketRegion.message)}
            </FormHelperText>
          )}
        </Box>

        <Box mb={2}>
          <TextField
            {...register(`${registerPrefix}.CTRBucketName`)}
            label={t("amazonConnect:ctrBucketName")}
            defaultValue={defaultValues.CTRBucketName}
            error={!!errors?.AmazonConnect?.CTRBucketName}
            autoComplete="off"
            required
            fullWidth
          />
          {errors?.AmazonConnect?.CTRBucketName?.message && (
            <FormHelperText error>
              {t(errors.AmazonConnect.CTRBucketName.message)}
            </FormHelperText>
          )}
        </Box>

        <Box mb={2}>
          <AWSRegionSelect
            label={t("amazonConnect:ctrBucketRegion")}
            {...register(`${registerPrefix}.CTRBucketRegion`, {
              required: true,
            })}
            defaultValue={defaultValues.CTRBucketRegion}
            error={!!errors?.AmazonConnect?.CTRBucketRegion}
            required
          />
          {errors?.AmazonConnect?.CTRBucketRegion?.message && (
            <FormHelperText error>
              {t(errors.AmazonConnect.CTRBucketRegion.message)}
            </FormHelperText>
          )}
        </Box>

        <Box mb={2}>
          <Controller
            control={control}
            name={`${registerPrefix}.ShouldUseExternalBucket`}
            render={({ field }) => (
              <FormControlLabel
                label={t("amazonConnect:shouldUseExternalBucket")}
                control={
                  <Checkbox
                    {...field}
                    onChange={(event) => {
                      field.onChange(event.target.checked);
                      updateExternalBucketFields(event.target.checked);
                    }}
                    checked={field.value}
                  />
                }
              />
            )}
          />
        </Box>

        {watch(`${registerPrefix}.ShouldUseExternalBucket`) && (
          <>
            <Box mb={2}>
              <TextField
                {...register(
                  `${registerPrefix}.RecordingDestinationBucketName`
                )}
                label={t("amazonConnect:recordingDestinationBucketName")}
                defaultValue={defaultValues.RecordingDestinationBucketName}
                error={!!errors?.AmazonConnect?.RecordingDestinationBucketName}
                autoComplete="off"
                fullWidth
              />
              {errors?.AmazonConnect?.RecordingDestinationBucketName
                ?.message && (
                <FormHelperText error>
                  {t(
                    errors.AmazonConnect.RecordingDestinationBucketName.message
                  )}
                </FormHelperText>
              )}
            </Box>

            <Box mb={2}>
              <AWSRegionSelect
                label={t("amazonConnect:recordingDestinationBucketRegion")}
                {...register(
                  `${registerPrefix}.RecordingDestinationBucketRegion`
                )}
                defaultValue={defaultValues.RecordingDestinationBucketRegion}
                error={
                  !!errors?.AmazonConnect?.RecordingDestinationBucketRegion
                }
              />
              {errors?.AmazonConnect?.RecordingDestinationBucketRegion
                ?.message && (
                <FormHelperText error>
                  {t(
                    errors.AmazonConnect.RecordingDestinationBucketRegion
                      .message
                  )}
                </FormHelperText>
              )}
            </Box>
          </>
        )}
      </FormGroup>

      <Typography variant="subtitle2" color="primary">
        {t("amazonConnect:prefixConfiguration")}
      </Typography>
      <Divider />

      <FormGroup>
        <Box mb={2}>
          <TextField
            {...register(`${registerPrefix}.CTRBucketPrefix`)}
            label={t("amazonConnect:ctrBucketPrefix")}
            defaultValue={defaultValues.CTRBucketPrefix}
            error={!!errors?.AmazonConnect?.CTRBucketPrefix}
            autoComplete="off"
            required
            fullWidth
          />
          {errors?.AmazonConnect?.CTRBucketPrefix?.message && (
            <FormHelperText error>
              {t(errors.AmazonConnect.CTRBucketPrefix.message)}
            </FormHelperText>
          )}
        </Box>

        <Box mb={2}>
          <TextField
            {...register(`${registerPrefix}.CallRecordingPrefix`)}
            label={t("amazonConnect:callRecordingPrefix")}
            defaultValue={defaultValues.CallRecordingPrefix}
            error={!!errors?.AmazonConnect?.CallRecordingPrefix}
            autoComplete="off"
            fullWidth
          />
          {errors?.AmazonConnect?.CallRecordingPrefix?.message && (
            <FormHelperText error>
              {t(errors.AmazonConnect.CallRecordingPrefix.message)}
            </FormHelperText>
          )}
        </Box>

        <Box mb={2}>
          <TextField
            {...register(`${registerPrefix}.ChatTranscriptPrefix`)}
            label={t("amazonConnect:chatTranscriptPrefix")}
            defaultValue={defaultValues.ChatTranscriptPrefix}
            error={!!errors?.AmazonConnect?.ChatTranscriptPrefix}
            autoComplete="off"
            fullWidth
          />
          {errors?.AmazonConnect?.ChatTranscriptPrefix?.message && (
            <FormHelperText error>
              {t(errors.AmazonConnect.ChatTranscriptPrefix.message)}
            </FormHelperText>
          )}
        </Box>

        <Box mb={2}>
          <TextField
            {...register(`${registerPrefix}.ScreenRecordingPrefix`)}
            label={t("amazonConnect:screenRecordingPrefix")}
            defaultValue={defaultValues.ScreenRecordingPrefix}
            error={!!errors?.AmazonConnect?.ScreenRecordingPrefix}
            autoComplete="off"
            fullWidth
          />
          {errors?.AmazonConnect?.ScreenRecordingPrefix?.message && (
            <FormHelperText error>
              {t(errors.AmazonConnect.ScreenRecordingPrefix.message)}
            </FormHelperText>
          )}
        </Box>
      </FormGroup>

      <Typography variant="subtitle2" color="primary">
        {t("amazonConnect:accessConfiguration")}
      </Typography>
      <Divider />

      <FormGroup>
        <Box mb={2}>
          <TextField
            {...register(`${registerPrefix}.AccessRoleARN`)}
            label={t("amazonConnect:accessRoleARN")}
            defaultValue={defaultValues.AccessRoleARN}
            error={!!errors?.AmazonConnect?.AccessRoleARN}
            autoComplete="off"
            required
            fullWidth
          />
          {errors?.AmazonConnect?.AccessRoleARN?.message && (
            <FormHelperText error>
              {t(errors.AmazonConnect.AccessRoleARN.message)}
            </FormHelperText>
          )}
        </Box>
      </FormGroup>
    </>
  );
}
